import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiSearch, FiChevronRight, FiAlertCircle, FiUsers } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { useGetWorkspaces, type WorkspaceListItem } from '../../api/workspaces';
import { useWorkspaceStore } from '../../store/useWorkspaceStore';
import { useAuth } from '../../hooks/useAuth';
import { extractApiError, isAuthenticationError } from '../../utils/apiError';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  editor: 'Editor',
};

// Workspaces shared with the current user (not owned by them).
const GroupIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="15"
    height="13"
    viewBox="0 0 15 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M1.33333 2.66602H0V11.3327C0 12.066 0.6 12.666 1.33333 12.666H12.6667V11.3327H1.33333V2.66602Z"
      fill="currentColor"
    />
    <path
      d="M13.3334 1.33333H8.66675L7.33342 0H4.00008C3.26675 0 2.67341 0.6 2.67341 1.33333L2.66675 8.66667C2.66675 9.4 3.26675 10 4.00008 10H13.3334C14.0667 10 14.6667 9.4 14.6667 8.66667V2.66667C14.6667 1.93333 14.0667 1.33333 13.3334 1.33333Z"
      fill="currentColor"
    />
  </svg>
);

// Workspaces the current user owns.
const FolderSupervisedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9.33337 13.3327V12.966C9.33337 12.466 9.57782 12.0688 10.0667 11.7743C10.5556 11.4799 11.2 11.3327 12 11.3327C12.8 11.3327 13.4445 11.4799 13.9334 11.7743C14.4223 12.0688 14.6667 12.466 14.6667 12.966V13.3327H9.33337ZM12 10.666C11.6334 10.666 11.3195 10.5355 11.0584 10.2743C10.7973 10.0132 10.6667 9.69935 10.6667 9.33268C10.6667 8.96602 10.7973 8.65213 11.0584 8.39102C11.3195 8.1299 11.6334 7.99935 12 7.99935C12.3667 7.99935 12.6806 8.1299 12.9417 8.39102C13.2028 8.65213 13.3334 8.96602 13.3334 9.33268C13.3334 9.69935 13.2028 10.0132 12.9417 10.2743C12.6806 10.5355 12.3667 10.666 12 10.666ZM2.66671 13.3327C2.30004 13.3327 1.98615 13.2021 1.72504 12.941C1.46393 12.6799 1.33337 12.366 1.33337 11.9993V3.99935C1.33337 3.63268 1.46393 3.31879 1.72504 3.05768C1.98615 2.79657 2.30004 2.66602 2.66671 2.66602H6.11671C6.29448 2.66602 6.46393 2.69935 6.62504 2.76602C6.78615 2.83268 6.92782 2.92713 7.05004 3.04935L8.00004 3.99935H13.3334C13.7 3.99935 14.0139 4.1299 14.275 4.39102C14.5362 4.65213 14.6667 4.96602 14.6667 5.33268V7.49935C14.2778 7.22157 13.8556 7.01324 13.4 6.87435C12.9445 6.73546 12.4778 6.66602 12 6.66602C10.7 6.66602 9.59726 7.11879 8.69171 8.02435C7.78615 8.9299 7.33337 10.0327 7.33337 11.3327C7.33337 11.6882 7.36948 12.0327 7.44171 12.366C7.51393 12.6993 7.62782 13.0216 7.78337 13.3327H2.66671Z"
      fill="currentColor"
    />
  </svg>
);

interface WorkspaceModalProps {
  onClose: () => void;
}

export const WorkspaceModal: React.FC<WorkspaceModalProps> = ({ onClose }) => {
  const [search, setSearch] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const { isAuthenticated } = useAuth();

  const { data, isLoading, isError, error, refetch, isRefetching } = useGetWorkspaces();
  const workspaces = useMemo(() => data?.results ?? [], [data]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isAuthenticated) {
      onClose();
      const currentUrl = window.location.pathname + (window.location.search || '');
      const separator = currentUrl.includes('?') ? '&' : '?';
      const redirectUrl = `${currentUrl}${separator}workspaceModal=true`;
      navigate({ to: '/sign-in', search: { redirect: redirectUrl } });
    }
  }, [isAuthenticated, navigate, onClose]);

  // Redirect if query fails due to authentication
  useEffect(() => {
    if (isError && error) {
      if (isAuthenticationError(error)) {
        onClose();
        const currentUrl = window.location.pathname + (window.location.search || '');
        const separator = currentUrl.includes('?') ? '&' : '?';
        const redirectUrl = `${currentUrl}${separator}workspaceModal=true`;
        navigate({ to: '/sign-in', search: { redirect: redirectUrl } });
      }
    }
  }, [isError, error, navigate, onClose]);

  // Auto-focus the search input on mount
  useEffect(() => {
    const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const accessibleWorkspaces = useMemo(
    () => workspaces.filter((w) => (w.role as string) !== 'guest'),
    [workspaces],
  );
  const personalWorkspace = useMemo(
    () => accessibleWorkspaces.find((w) => w.is_personal),
    [accessibleWorkspaces],
  );
  const sharedWorkspaces = useMemo(
    () => accessibleWorkspaces.filter((w) => !w.is_personal),
    [accessibleWorkspaces],
  );

  const filteredShared = sharedWorkspaces.filter((w) =>
    w.name.toLowerCase().includes(search.toLowerCase()),
  );

  const showPersonal =
    !!personalWorkspace &&
    (!search || personalWorkspace.name.toLowerCase().includes(search.toLowerCase()));

  const handleSelect = (workspace: WorkspaceListItem) => {
    setActiveWorkspace(workspace.uuid, workspace.name);
    onClose();
    navigate({ to: '/projects', search: { workspace: workspace.uuid } });
  };

  const renderWorkspaceRow = (ws: WorkspaceListItem) => (
    <button
      key={ws.uuid}
      onClick={() => handleSelect(ws)}
      className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-200 hover:bg-white/5 cursor-pointer group"
    >
      <div className="w-10 h-10 rounded-xl bg-zinc-800 border border-white/10 flex items-center justify-center shrink-0">
        {ws.role === 'owner' ? (
          <FolderSupervisedIcon className="text-zinc-400" />
        ) : (
          <GroupIcon className="text-zinc-400" />
        )}
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-white text-[15px] font-medium font-['Inter'] truncate">{ws.name}</p>
        <p className="text-zinc-500 text-[12px] font-['Inter'] mt-0.5 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1">
            <FiUsers size={11} className="shrink-0" />
            {ws.project_count} project{ws.project_count !== 1 ? 's' : ''}
          </span>
          <span className="text-zinc-700">·</span>
          <span className="uppercase tracking-wide font-semibold text-zinc-400">
            {ROLE_LABEL[ws.role] ?? ws.role}
          </span>
        </p>
      </div>
      <FiChevronRight
        size={16}
        className="text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0"
      />
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ animation: 'fadeIn 0.15s ease-out' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4 bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.7)] overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-white text-lg font-semibold font-['Inter'] tracking-tight">
            Choose a workspace
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer group"
          >
            <FiX size={18} className="text-zinc-400 group-hover:text-white transition-colors" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pb-4">
          <div className="relative flex items-center bg-[#111] border border-white/10 rounded-xl px-4 h-11 transition-colors focus-within:border-white/20">
            <FiSearch size={16} className="text-zinc-500 shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="ml-3 bg-transparent border-none text-white text-[14px] font-['Inter'] placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
            />
          </div>
        </div>

        {/* Workspace list */}
        <div className="px-3 pb-3 max-h-80 overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="space-y-1 px-1">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3.5">
                  <div className="w-10 h-10 rounded-xl bg-white/5 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 rounded bg-white/5 animate-pulse" />
                    <div className="h-2.5 w-20 rounded bg-white/5 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-3 px-4">
              <FiAlertCircle className="w-7 h-7 text-rose-450" />
              <p className="text-zinc-400 text-xs">
                {extractApiError(error, 'Failed to load workspaces.')}
              </p>
              <button
                onClick={() => refetch()}
                disabled={isRefetching}
                className="px-4 py-2 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 text-xs font-semibold transition-all cursor-pointer disabled:opacity-50"
              >
                {isRefetching ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          ) : (
            <>
              {/* Personal workspace */}
              {showPersonal && personalWorkspace && renderWorkspaceRow(personalWorkspace)}

              {/* Shared workspaces */}
              {filteredShared.length > 0 && (
                <>
                  <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.15em] px-4 pt-4 pb-2">
                    Shared with me
                  </p>
                  {filteredShared.map(renderWorkspaceRow)}
                </>
              )}

              {/* Empty state */}
              {!showPersonal && filteredShared.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-zinc-500 text-sm font-['Inter']">
                    {workspaces.length === 0 ? 'No workspaces found' : 'No matches found'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}</style>
    </div>,
    document.body,
  );
};
